import { useEffect, useState } from "preact/hooks";
import { feel } from "../feel/kit.js";
import { reduceMotion } from "../settings/settings.js";

/**
 * Moments (P5-UX-03): the reward felt on the result. Stars fill one by one, beans count up and the
 * level bar fills, then a level up and an achievement each get their sound, once. Short: all done
 * within about two and a half seconds, and a tap or a key anywhere lands everything at once. With
 * less motion on, the same information shows at once, still, with one sound. Nothing here
 * covers the next-step button (ADR 0043): it all sits in the result's own flow.
 */

/** When each part of a result starts (ms after it shows), so they follow each other. */
export const AT = { stars: 300, beans: 1300, level: 2100, feats: 2300 } as const;
/** Between two stars (ms). */
export const STAR_GAP_MS = 350;
/** How long the beans take to count up (ms). */
export const COUNT_MS = 700;

/**
 * Runs `steps` at their times unless the player wants less motion; a tap or a key press, or less
 * motion, runs what's left at once. Each step runs once.
 */
export function useMoment(steps: readonly { at: number; run: (skipped: boolean) => void }[]) {
  useEffect(() => {
    const left = new Set(steps.map((_, i) => i));
    const runOne = (i: number, skipped: boolean) => {
      if (!left.delete(i)) return;
      steps[i]?.run(skipped);
    };
    const finish = () => {
      for (const i of [...left]) runOne(i, true);
      stop();
    };
    const timers = steps.map((s, i) =>
      setTimeout(() => {
        runOne(i, false);
      }, s.at),
    );
    const stop = () => {
      timers.forEach(clearTimeout);
      document.removeEventListener("pointerdown", finish, true);
      document.removeEventListener("keydown", finish, true);
    };
    if (reduceMotion.peek()) {
      finish();
      return;
    }
    document.addEventListener("pointerdown", finish, true);
    document.addEventListener("keydown", finish, true);
    return stop;
  }, []);
}

/** A result's stars, filling one by one, each with its sound; read out as the stars won. */
export function StarsMoment({ n }: { n: number | undefined }) {
  const won = n === undefined || n < 1 ? 0 : Math.min(3, Math.round(n));
  const [shown, setShown] = useState(0);
  useMoment(
    Array.from({ length: won }, (_, i) => ({
      at: AT.stars + i * STAR_GAP_MS,
      run: (skipped: boolean) => {
        setShown((s) => Math.max(s, i + 1));
        // Skipped, the stars land at once with one sound, not three on top of each other.
        if (!skipped || i === won - 1) feel("star");
      },
    })),
  );
  if (won === 0) return null;
  return (
    <span
      class="stars moment"
      role="img"
      aria-label={`${String(won)} of 3 stars`}
      data-shown={shown}
    >
      {[0, 1, 2].map((i) => (
        <span key={i} class={i < shown ? "on" : i < won ? "coming" : "off"} aria-hidden="true">
          {i < shown ? "★" : "☆"}
        </span>
      ))}
    </span>
  );
}

/** A number that counts up to `to` from `from` once its moment comes (or lands at once). */
export function useCount(from: number, to: number, at: number): number {
  const [value, setValue] = useState(from);
  useMoment([
    {
      at,
      run: (skipped) => {
        if (skipped || to <= from) {
          setValue(to);
          return;
        }
        const start = performance.now();
        const tick = () => {
          const t = Math.min(1, (performance.now() - start) / COUNT_MS);
          setValue(Math.round(from + (to - from) * (1 - (1 - t) ** 3)));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
    },
  ]);
  return value;
}
